import { Close, Done } from "@mui/icons-material";
import { Alert, Box, Button, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import dayjs from "dayjs";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import { useEffect, useMemo, useRef, useState } from "react";
import MallScanner from "src/components/shared/ScanningInput";
import TableComp from "src/components/shared/table/TableComp";
import useAppGlobal from "src/context/useAppGlobal";
import useBatchCodeUpdate from "src/hooks/batch/useBatchCodeUpdate";
import useBatchDetail from "src/hooks/batch/useBatchDetail";
import useProductDetail from "src/hooks/product/useProductDetail";
import useSettingBatchCode from "src/hooks/setting/batch-code/useSettingBatchCode";
import useDebounce from "src/hooks/useDebounce";

export default function ScanItemCodesDialog({ item, batchDetail, mutate }) {
  const { closeDialog, setDialogSize, size } = useAppGlobal();
  const inputRef = useRef();
  const [itemCodes, setItemCodes] = useState([]);
  const [pagi, setPagi] = useState({ limit: 20, skip: 0 });
  const [codes, setCodes] = useState([]);
  const [errorCode, setErrorCode] = useState(null);
  const [isFulled, setIsFulled] = useState(false);
  const fulledCloseTimerRef = useRef(null);
  const debouncedValue = useDebounce(itemCodes, 180);
  const { trigger: pushBatch } = useBatchCodeUpdate(item?._id);
  const { data: codeList } = useBatchDetail(item?._id);
  const { data: batchCode } = useSettingBatchCode();
  const { data: productDetail } = useProductDetail(
    !!item?.product?._id ? item?.product?._id : item?.product,
    !!item?.productModel?._id ? item?.productModel?._id : item?.productModel,
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      inputRef.current?.focus();
    }, 3000);

    return () => clearTimeout(timeout);
  }, []);

  const handlePushCode = async () => {
    const res = await pushBatch({
      body: {
        itemCodes: codes,
      },
    });
    if (res?.success) {
      if (res?.data?.totalError > 0) {
        handleConfirm(res?.data);
        setErrorCode(res?.data);
      }
      if (res?.data?.totalError === 0) {
        setCodes([]);
        setItemCodes("");
      }
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "warning" });
    }
  };

  const handleFilterErrorCode = (value, key) => {
    setItemCodes("");
    setCodes([]);
    setErrorCode(null);
    closeSnackbar(key);
  };

  const handleConfirm = (errorObj) => {
    const action = (key) => (
      <Button
        onClick={() => {
          handleFilterErrorCode(errorObj, key);
        }}
        sx={{ bgcolor: "var(--black)" }}
        variant="contained"
        size="small"
      >
        Xác nhận
      </Button>
    );
    return enqueueSnackbar(
      <div>
        <p style={{ margin: 0 }}>Vui lòng xác nhận xoá các mã lỗi:</p>
        {!!errorObj?.errorItemCodes?.createFailed?.length && (
          <p style={{ margin: 0 }}>
            Mã lỗi: <strong>{String(errorObj?.errorItemCodes?.createFailed)}</strong>
          </p>
        )}
        {!!errorObj?.errorItemCodes?.duplicate?.length && (
          <p style={{ margin: 0 }}>
            Mã trùng: <strong>{String(errorObj?.errorItemCodes?.duplicate)}</strong>
          </p>
        )}
        {!!errorObj?.errorItemCodes?.existed?.length && (
          <p style={{ margin: 0 }}>
            Mã đã tồn tại: <strong>{String(errorObj?.errorItemCodes?.existed)}</strong>
          </p>
        )}
        {!!errorObj?.errorItemCodes?.invalidPrefix?.length && (
          <p style={{ margin: 0 }}>
            Sai format: <strong>{String(errorObj?.errorItemCodes?.invalidPrefix)}</strong>
          </p>
        )}
      </div>,
      {
        variant: "warning",
        action,
        persist: true,
        preventDuplicate: true,
        anchorOrigin: { vertical: "top", horizontal: "center" },
      },
    );
  };

  useEffect(() => {
    if (!debouncedValue?.itemCodes) return;
    const refinedArray = debouncedValue?.itemCodes?.map((v) => v?.replaceAll("Shift", ""));
    if (productDetail?.data?.productGroup?.skipValidItemCode) {
      setCodes((prev) => [...prev, ...refinedArray]);
    } else {
      if (!Array.isArray(batchCode?.data?.prefixItemCodes) || !Array.isArray(batchCode?.data?.stripItemCodes)) return;
      const validCodes = [];
      const invalidCodes = [];

      refinedArray.forEach((line) => {
        let cleaned = line;

        batchCode.data.stripItemCodes.forEach((strip) => {
          if (cleaned.startsWith(strip + "/")) {
            cleaned = cleaned.replace(strip + "/", "");
          } else if (cleaned.startsWith(strip)) {
            cleaned = cleaned.replace(strip, "");
          }
        });

        const hasValidPrefix = batchCode.data.prefixItemCodes.some((prefix) => cleaned.startsWith(prefix));

        if (hasValidPrefix) {
          validCodes.push(cleaned);
        } else {
          invalidCodes.push(cleaned);
        }
      });

      if (validCodes.length > 0) {
        setCodes((prev) => [...prev, ...validCodes]);
      }

      if (invalidCodes.length > 0) {
        setItemCodes();
        enqueueSnackbar(`Mã không hợp lệ: ${invalidCodes.join(", ")}`, {
          variant: "warning",
        });
      }
    }
  }, [debouncedValue?.itemCodes, batchCode?.data?.prefixItemCodes, batchCode?.data?.stripItemCodes, productDetail?.data?.productGroup?.skipValidItemCode]);

  useEffect(() => {
    if (!!codes?.length) {
      handlePushCode();
    }
  }, [codes?.length]);

  useEffect(() => {
    if (codes?.length === 10) {
      enqueueSnackbar("Hệ thống đang xử lý...", { variant: "warning" });
    }
  }, [codes?.length]);

  // Không khai báo mutate trong deps (SWR có thể đổi reference) để tránh cleanup chạy lặp.
  useEffect(() => {
    if (isFulled) {
      setDialogSize("xs");
      // fulledCloseTimerRef.current = setTimeout(() => {
      //   fulledCloseTimerRef.current = null;
      //   closeDialog();
      //   mutate();
      // }, 2500);
      return () => {
        setDialogSize("xl");
        if (fulledCloseTimerRef.current) {
          clearTimeout(fulledCloseTimerRef.current);
          fulledCloseTimerRef.current = null;
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutate từ SWR đổi reference gây cleanup lặp vô hạn
  }, [isFulled, closeDialog, setDialogSize]);

  const handleClose = () => {
    if (codeList?.data?.totalItemCodes === (item?.qty || batchDetail?.qty)) {
      mutate();
      setIsFulled(true);
    } else {
      closeDialog();
    }
  };

  const typeName = useMemo(() => {
    const productModelId = !!item?.productModel?._id ? item?.productModel?._id : item?.productModel;
    return productDetail?.data?.productModels?.find((v) => v?._id === productModelId)?.name;
  }, [item?.productModel, productDetail?.data?.productModels]);

  const handleScanComplete = () => {
    setIsFulled(true);
  };

  return (
    <>
      {!isFulled ? (
        <DialogTitle
          fontWeight="bold"
          display="flex"
          justifyContent="space-between"
          sx={{ borderBottom: !isFulled ? "1px solid var(--grey)" : "none", mb: "8px" }}
        >
          <Typography fontWeight="bold">Scan mã sản phẩm</Typography>
          <IconButton
            onClick={closeDialog}
            sx={{ width: "24px", height: "24px" }}
          >
            <Close sx={{ fontSize: "22px" }} />
          </IconButton>
        </DialogTitle>
      ) : (
        <></>
      )}

      <DialogContent sx={(isFulled ? { pt: 3, pb: 1 } : undefined, { padding: "30px !important" })}>
        {!isFulled || size !== "xs" ? (
          <Grid
            container
            spacing={4.5}
          >
            {codes?.length === 10 && (
              <Grid
                xs={12}
                item
              >
                <Alert
                  severity="warning"
                  variant="filled"
                >
                  Hệ thống đang xử lý...{" "}
                </Alert>
              </Grid>
            )}
            <Grid
              item
              xs={6}
            >
              <Typography mb="12px">
                <strong>Số lô (LOT): </strong>
                {item?.code}
              </Typography>
              <Typography mb="12px">
                <strong>Tên sản phẩm: </strong>
                {productDetail?.data?.name ?? `${item?.product?.name} - ${item?.productModel?.name}`}
              </Typography>
              <Typography mb="12px">
                <strong>Phân loại: </strong>
                {typeName}
              </Typography>
              <Typography mb="12px">
                <strong>Số lượng: </strong> {item?.qty}
              </Typography>
              <Typography mb="12px">
                <strong>Ngày sản xuất (MFG): </strong>
                {dayjs(item?.mfg).format("DD-MM-YYYY")}
              </Typography>
              <Typography mb="12px">
                <strong>Ngày hết hạn (EXP): </strong>
                {dayjs(item?.exp).format("DD-MM-YYYY")}
              </Typography>
              <Typography mb="12px">
                <strong>Đã scan: </strong> {codeList?.data?.totalItemCodes ?? 0}/{item?.qty || batchDetail?.qty || 0}
              </Typography>
              <Tooltip
                title={!batchCode?.data?.prefixItemCodes ? "Chưa có mã vạch sản phẩm" : ""}
                placement="top"
              >
                <MallScanner
                  full={codeList?.data?.totalItemCodes !== 0 && codeList?.data?.totalItemCodes === (item?.qty || batchDetail?.qty)}
                  getValue={setItemCodes}
                  value={itemCodes}
                  disabled={codes?.length === 10 || !batchCode?.data?.prefixItemCodes || !!errorCode}
                  onScanComplete={handleScanComplete}
                />
              </Tooltip>
            </Grid>
            <Grid
              item
              xs={6}
            >
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", mb: "12px" }}
              >
                Danh sách chờ hệ thống xử lý
              </Typography>
              <TableComp
                height="62vh"
                // dynamicHeight="80vh"
                pagi={pagi}
                total={codeList?.count}
                setPagi={setPagi}
                dynamicWidth={"400px"}
                // dynamicHeight={"400px"}
                bodyList={
                  Array.isArray(codes)
                    ? codes?.map((v, i) => ({
                        no: i + 1 + pagi?.skip,
                        code: v,
                      }))
                    : []
                }
                sortData={["no", "code"]}
                headers={[
                  {
                    id: 1,
                    title: "STT",
                    align: "center",
                  },
                  {
                    id: 2,
                    title: "Mã sản phẩm (SKU)",
                    align: "center",
                  },
                ]}
              />
            </Grid>
          </Grid>
        ) : (
          <Box
            sx={{
              maxWidth: 400,
              width: "100%",
              mx: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={2}
                sx={{
                  background: "var(--success)",
                  borderRadius: "100px",
                }}
              >
                <Done
                  sx={{
                    fontSize: 28,
                    color: "var(--success-txt)",
                    width: "42px",
                    height: "42px",
                    border: "4px solid var(--success-txt)",
                    borderRadius: "100px",
                    background: "var(--success)",
                  }}
                />
              </Box>
            </Box>
            <Typography
              align="center"
              fontSize="24px"
              fontWeight={700}
              sx={{ m: 0 }}
            >
              Cập nhật Thành công
            </Typography>
            <Typography
              align="center"
              variant="body2"
              color="text.secondary"
              sx={{ m: 0, lineHeight: 1.5 }}
            >
              Số lô <strong>{item?.code}</strong> đã được cập nhật thành công
            </Typography>
            <Button
              variant="contained"
              fullWidth
              sx={{
                mt: 2,
                width: "50%",
                maxWidth: {
                  xs: 200,
                  md: 300,
                },
              }}
              onClick={() => closeDialog()}
            >
              Đóng
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={!isFulled ? { p: "0 24px 24px" } : { p: "0 !important" }}>
        {!isFulled && (
          <Button
            onClick={handleClose}
            sx={{ width: "100%" }}
            variant="contained"
          >
            Lưu file
          </Button>
        )}
      </DialogActions>
    </>
  );
}
